import { Trash2 } from 'lucide-react';

import { IconButton } from '@/components/ui/icon-button';

/**
 * The app's remove control, which is an ARMED TRASH — not a bare X.
 *
 * X appears nowhere else in the editor for deletion: every other removable row, from an MCQ option
 * to a targeted feedback card, is a `Trash2` that arms on the first click and fires on the second.
 * A different glyph with a different number of clicks for the same act is two things to learn.
 *
 * Disarms on blur and on pointer-leave, the same as `IconButton`'s other callers: a control left
 * armed because the teacher looked away is one that fires on a click meant for something else.
 *
 * Lifted out of the condition list once the change card's effect rows needed the same control.
 * They had shipped with the bare X this replaced, which is exactly how a second grammar gets in:
 * the rule lived in a comment beside one private copy, where the next row to be written could not
 * read it.
 */
export function FlowRemoveButton({ label, confirmLabel, armed, onArmedChange, onClick }: {
  label: string; confirmLabel: string;
  /** Owned by the ROW, so the whole row can show what is about to go — see `ARMED_ROW`. */
  armed: boolean;
  onArmedChange: (armed: boolean) => void;
  onClick: () => void;
}) {
  return (
    <IconButton
      type='button'
      size='sm'
      variant='destructive'
      isArmed={armed}
      onClick={() => { if (armed) { onArmedChange(false); onClick(); } else onArmedChange(true); }}
      onPointerLeave={(e) => { if (armed && e.pointerType !== 'touch') onArmedChange(false); }}
      onBlur={() => onArmedChange(false)}
      aria-label={armed ? confirmLabel : label}
      title={armed ? confirmLabel : label}
    >
      <Trash2 aria-hidden='true' className='h-3.5 w-3.5' />
    </IconButton>
  );
}

/**
 * The outline a row wears while its delete is armed.
 *
 * Outranks a problem tint: "this is about to be deleted" is the more urgent fact, and it answers
 * the question the second click is asking. Matches `IconButton`'s own armed tint and the affiliation
 * cards, so a teacher who has met the two-click grammar anywhere else already knows this one.
 */
export const ARMED_ROW = 'border-ws-warning-fg bg-ws-warning-bg/40';
